import { useRef, useMemo, useState } from 'react';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';

import { Button, FormControl, InputLabel, Select, TextField, makeStyles } from '@material-ui/core';
import { PrintRounded as PrintIcon } from '@material-ui/icons';

import { useGetOrdersToArchive } from '../hooks/get-orders-to-archive.hook';
import { useArchiveOrders } from '../hooks/archive-orders.hook';
import { useUnarchiveOrders } from '../hooks/unarchive-orders.hook';
import { useGetLabelsByDate, type LabelsData } from '../hooks/get-labels-by-date.hook';
import { PrintLabelsContainer } from '../components/Print/PrintLabelsContainer.component';
import {
  OrderTableWithDetails,
  type OrderTableHeader,
  type OrderTableRow,
} from '../components/Tables/OrderTableWithDetails.component';
import { mapOrderToTableRow } from '../utils/map-order-to-table-row.util';

const USER_ROLES = [
  { value: 'all', label: 'Tous' },
  { value: 'user', label: 'Utilisateur' },
  { value: 'subscriberBtoC', label: 'Abonne BtoC' },
  { value: 'subscriberBtoB', label: 'Abonne BtoB' },
  { value: 'eco', label: 'Eco' },
  { value: 'conciergerie', label: 'Conciergerie' },
  { value: 'admin', label: 'Admin' },
  { value: 'superAdmin', label: 'Super Admin' },
] as const;

const ORDER_TABLE_HEADERS: OrderTableHeader[] = [
  { key: 'owner', label: 'Client', minWidth: 250, align: 'left' },
  { key: 'status', label: 'Statut', minWidth: 150, align: 'left' },
  { key: 'advStatus', label: 'ADV', minWidth: 150, align: 'left' },
  { key: 'totalHt', label: 'Total HT', minWidth: 100, align: 'right' },
  { key: 'totalTtc', label: 'Total TTC', minWidth: 100, align: 'right' },
  { key: 'deliveryDay', label: 'Livraison le', minWidth: 100, align: 'left' },
  { key: 'created', label: 'Création le', minWidth: 150, align: 'right' },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginLeft: 'auto',
  },

  primaryButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

interface ActionContainerProps {
  deliveryDate: string;
  ownerRole: string;
  loading: boolean;
  archiving: boolean;
  unarchiving: boolean;
  hasOrders: boolean;
  onDeliveryDateChange: (value: string) => void;
  onOwnerRoleChange: (value: string) => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onPrint: () => void;
}

function ActionContainer({
  deliveryDate,
  ownerRole,
  loading,
  archiving,
  unarchiving,
  hasOrders,
  onDeliveryDateChange,
  onOwnerRoleChange,
  onArchive,
  onUnarchive,
  onPrint,
}: ActionContainerProps) {
  const classes = useStyles();

  return (
    <section className={classes.filters}>
      <TextField
        size="small"
        label="Date de livraison"
        type="date"
        variant="outlined"
        value={deliveryDate}
        onChange={(e) => onDeliveryDateChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <FormControl variant="outlined" size="small">
        <InputLabel>Role</InputLabel>
        <Select
          native
          label="Role"
          value={ownerRole}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onOwnerRoleChange(e.target.value)}
        >
          {USER_ROLES.map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </Select>
      </FormControl>
      <div className={classes.actions}>
        <Button size="small" disabled={loading || unarchiving} onClick={onUnarchive}>
          {unarchiving ? 'Annulation...' : 'Désarchiver'}
        </Button>
        <Button
          size="small"
          startIcon={<PrintIcon />}
          variant="outlined"
          disabled={loading || archiving || !hasOrders}
          onClick={onArchive}
        >
          {archiving ? 'Archivage...' : 'Archiver & Imprimer'}
        </Button>
        <Button
          size="small"
          startIcon={<PrintIcon />}
          variant="contained"
          className={classes.primaryButton}
          disabled={loading || archiving}
          onClick={onPrint}
        >
          Imprimer
        </Button>
      </div>
    </section>
  );
}

export default function AdminArchiveOrdersView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [deliveryDate, setDeliveryDate] = useState<string>(moment().add(1, 'days').format('YYYY-MM-DD'));
  const [ownerRole, setOwnerRole] = useState<string>('all');

  const { loading, orders, fetchOrders } = useGetOrdersToArchive({ deliveryDate, ownerRole });
  const { archiving, archiveOrders } = useArchiveOrders();
  const { unarchiving, unarchiveOrders } = useUnarchiveOrders();
  const { fetchLabels } = useGetLabelsByDate();
  const [labels, setLabels] = useState<LabelsData | null>(null);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrintLabels = useReactToPrint({ content: () => printRef.current });

  const tableRows: OrderTableRow[] = useMemo(() => orders.map(mapOrderToTableRow), [orders]);

  const handleDeliveryDateChange = (value: string) => {
    setDeliveryDate(value);
    setPage(0);
  };

  const handleOwnerRoleChange = (value: string) => {
    setOwnerRole(value);
    setPage(0);
  };

  const handleArchive = async () => {
    const response = await archiveOrders(deliveryDate, ownerRole, true);
    if (response) {
      if (response.labels && (response.labels.products.length > 0 || response.labels.baskets.length > 0)) {
        setLabels(response.labels);
        setTimeout(() => handlePrintLabels(), 100);
      }
      fetchOrders();
    }
  };

  const handlePrintOnly = async () => {
    const labelsData = await fetchLabels(deliveryDate, ownerRole);
    if (labelsData && (labelsData.products.length > 0 || labelsData.baskets.length > 0)) {
      setLabels(labelsData);
      setTimeout(() => handlePrintLabels(), 100);
    }
  };

  const handleUnarchive = async () => {
    const success = await unarchiveOrders(deliveryDate, ownerRole);
    if (success) fetchOrders();
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      <h2>Commandes à archiver</h2>
      <ActionContainer
        deliveryDate={deliveryDate}
        ownerRole={ownerRole}
        loading={loading}
        archiving={archiving}
        unarchiving={unarchiving}
        hasOrders={orders.length > 0}
        onDeliveryDateChange={handleDeliveryDateChange}
        onOwnerRoleChange={handleOwnerRoleChange}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onPrint={handlePrintOnly}
      />
      <OrderTableWithDetails
        maxHeight="75svh"
        headers={ORDER_TABLE_HEADERS}
        orders={tableRows}
        totalCount={orders.length}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={loading}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
      <PrintLabelsContainer ref={printRef} labels={labels} />
    </section>
  );
}
